import i18next from 'i18next';
import HttpApi from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import ICU from 'i18next-icu';

import { formats } from './formats';
import { lngWhiteList } from '../../src/utilities/localization';

// Custom language detector that reads from localStorage.
// For en-US variants (en-US-k12, en-US-pro), strips the suffix
// so i18next uses 'en-US' as the language (required for valid ICU locale).
const localStorageLngDetector = new LanguageDetector();
localStorageLngDetector.addDetector({
    name: 'localeStorageLngDetector',
    lookup() {
        let found = window.localStorage.getItem('selectedLocale') ?? undefined;
        if (found) {
            const chunks = found.split('-');
            if (chunks.length === 3) {
                found = `${chunks[0]}-${chunks[1]}`;
            }
        }
        return found;
    },
});

// Derives the en-US variant suffix directly from localStorage at load time,
// so it always reflects the current selection without needing page reload.
const getEnUSSuffix = (): string => {
    const selectedLocale = window.localStorage.getItem('selectedLocale') || '';
    const chunks = selectedLocale.split('-');
    // Expected formats: "en-US", "en-US-k12", "en-US-pro"
    if (chunks.length === 3 && `${chunks[0]}-${chunks[1]}` === 'en-US') {
        const suffix = chunks[2];
        // Prevent path traversal / unexpected characters from localStorage
        if (/^[A-Za-z0-9]+$/.test(suffix)) {
            return `-${suffix}`;
        }
    }
    return '';
};

const detectorOptions = {
    // first it attempts to load from the browser's local storage
    // then it attempts getting locale that is set in the user's browser
    order: ['localeStorageLngDetector', 'navigator'],
};

const getAppBuildEnvironment = () =>
    !process.env.NODE_ENV || process.env.NODE_ENV === 'development';

i18next
    .use(HttpApi)
    .use(localStorageLngDetector)
    .use(initReactI18next)
    .use(new ICU({ formats, bindI18nStore: 'added' }))
    .init({
        backend: {
            loadPath: (lng: string[]) =>
                `/config/locales/${lng[0]}${
                    lng[0] === 'en-US' ? getEnUSSuffix() : ''
                }.json`,
            parse: (data: string) => {
                const json = JSON.parse(data);
                if (typeof json === 'string') {
                    throw new Error(
                        'Unable to parse application translation file to JSON.',
                    );
                }
                return json;
            },
        },
        detection: detectorOptions,
        supportedLngs: lngWhiteList,
        nonExplicitSupportedLngs: true,
        load: 'all',
        fallbackLng: 'en', // FallbackLngObjList is not supported in I18nProvider right now
        debug: getAppBuildEnvironment(),
        interpolation: {
            escapeValue: false,
            formatSeparator: ',',
        },
        react: {
            useSuspense: true,
        },
    });

export default i18next;
